import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { Button, ButtonModel, IconRegularCheckmark, IconSolidStar } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react'

// hooks
import useMessages from '../../../hooks/useMessages'

// styles
import * as SC from './Step4SummaryStyles'
import { BREAKPOINTS } from '../../../styles/constants'

// components
import AddToCalendarModal from '../../AddToCalendarModal/AddToCalendarModal'
import ErrorScreen from '../../../atoms/ErrorScreen/ErrorScreen'
import InfoLine from '../../../atoms/InfoLine/InfoLine'
import Tooltip from '../../Tooltip/Tooltip'
import VisuallyHidden from '../../../atoms/VisuallyHidden/VisuallyHidden'

// assets
import CalendarPlusIcon from '../../../assets/icons/CalendarPlusIcon'
import CheckmarkIcon from '../../../assets/icons/CheckmarkIcon'
import CloseIcon from '../../../assets/icons/CloseIcon'

// types
import {
	CmsThankYouPageBannerData,
	PostCartSubmitReservation,
	PostCartSubmitSuccessResponse,
	ReservationBookingModalCloseType,
	SalonServiceResponse
} from '../../../types/types'

// utils
import { getModalNextReservationEvent, getModalReservationPurchaseEvent, getPromotionEvent } from '../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../utils/dataLayer'
import { formatRating, formatRelativeDate, getEmployeeName, getLinkAttributes, PAGE_LINKS } from '../../../utils/helper'
import { MyLocationContext } from '../../../utils/myLocationProvider'
import { RESERVATION_STATE } from '../../../utils/enums'
import { INFO_LINE_TYPE } from '../../../atoms/InfoLine/types'
import { AppContext } from '../../../utils/appProvider'
import {
	getReservationDisplayState,
	getReservationStatusTooltipContent,
	hasValidStep4SummaryData,
	renderReservationStatusTooltipOverlay
} from './step4SummaryHelpers'

type Props = PropsWithChildren<{
	onClose: (closeType: ReservationBookingModalCloseType) => void
	onRedirectToHomepage: () => void
	submittedReservationData: PostCartSubmitSuccessResponse
	selectedServiceData: SalonServiceResponse['service']
	cmsData: CmsThankYouPageBannerData
}>

const Step4Summary = (props: Props) => {
	const { onClose, onRedirectToHomepage, submittedReservationData, selectedServiceData, cmsData } = props
	const { image: promoBanner } = cmsData

	const { messages } = useMessages()
	const intl = useIntl()
	const { formatMessage, formatDate, locale } = intl
	const { myLocation } = useContext(MyLocationContext)
	const { productionEnvironment } = useContext(AppContext)

	const { customerNote, customer, visitor, visitorSameAsCustomer, reservations } = submittedReservationData
	const hasSummaryData = hasValidStep4SummaryData(submittedReservationData)
	const hasVisitorSection = !visitorSameAsCustomer && !!visitor
	const hasCustomerNoteSection = !!customerNote

	const [calendarReservation, setCalendarReservation] = useState<PostCartSubmitReservation | null>(null)

	const isPurchaseDataLayerEventSend = useRef(false)
	const isPromoBannerViewDataLayerEventSend = useRef(false)

	const scrollableContentRef = useRef<OverlayScrollbarsComponentRef<React.ElementType>>(null)
	const promoBannerRef = useRef<HTMLDivElement>(null)
	const titleRef = useRef<HTMLHeadingElement>(null)

	useEffect(() => {
		if (!hasSummaryData) {
			return
		}

		titleRef.current?.focus()
	}, [hasSummaryData])

	useEffect(() => {
		if (isPurchaseDataLayerEventSend.current || !hasSummaryData) {
			return
		}
		pushToDataLayer(
			getModalReservationPurchaseEvent({
				postReservationData: submittedReservationData,
				industryID: selectedServiceData.category.id,
				secondLevelCategoryID: selectedServiceData.category.child.id,
				serviceCategoryID: selectedServiceData.category.child.child?.id ?? ''
			})
		)
		isPurchaseDataLayerEventSend.current = true
	}, [
		hasSummaryData,
		submittedReservationData,
		selectedServiceData.category.id,
		selectedServiceData.category.child.id,
		selectedServiceData.category.child.child?.id
	])

	// Tracks promo banner view event using IntersectionObserver. Sends the event once when the banner is 50% visible.
	useEffect(() => {
		if (!promoBannerRef.current || !scrollableContentRef.current) {
			return undefined
		}

		const osInstance = scrollableContentRef.current.osInstance?.()
		const rootElement = osInstance?.elements().viewport

		if (!rootElement) {
			return undefined
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.intersectionRatio >= 0.5 && !isPromoBannerViewDataLayerEventSend.current) {
					pushToDataLayer(
						getPromotionEvent('view_promotion', [
							{ code: promoBanner.trackingID, location: 'booking_type', position: 1, destination_url: promoBanner.link || 'unclickable' }
						])
					)
					isPromoBannerViewDataLayerEventSend.current = true
					observer.disconnect()
				}
			},
			{
				root: rootElement,
				threshold: [0.5]
			}
		)

		observer.observe(promoBannerRef.current)

		return () => observer.disconnect()
	}, [promoBanner.trackingID, promoBanner.link])

	const footerButtons = (
		<>
			<Button
				onClick={() => {
					pushToDataLayer(getModalNextReservationEvent())
					onClose(null)
				}}
				fullWidth
				buttonStyle={ButtonModel.Styles.secondary}
			>
				{messages['Next reservation']}
			</Button>
			<SC.LinkButton
				href={PAGE_LINKS['/salons'](locale, { ...myLocation })}
				onClick={() => {
					onRedirectToHomepage()
				}}
				fullWidth
				buttonStyle={ButtonModel.Styles.primary}
			>
				{messages['Close booking modal']}
			</SC.LinkButton>
		</>
	)

	const promoBannerPicture = (
		<SC.Picture
			onClick={() => {
				// NOTE from Petr Haken: select_promotion prosim poslat, pokud se uzivatel snazi nekterou z tech ploch prokliknout (at uz to jde nebo nejde)
				pushToDataLayer(
					getPromotionEvent('select_promotion', [
						{
							code: promoBanner.trackingID,
							location: 'booking_type',
							position: 1,
							destination_url: promoBanner.link || 'unclickable'
						}
					])
				)
			}}
		>
			{/* WebP variants */}
			{promoBanner.desktopWebP && <source srcSet={promoBanner.desktopWebP} type='image/webp' media={`(min-width: ${BREAKPOINTS.lg})`} />}
			{promoBanner.mobileWebP && <source srcSet={promoBanner.mobileWebP} type='image/webp' />}

			{/* JPEG fallback */}
			<source srcSet={promoBanner.desktop} media={`(min-width: ${BREAKPOINTS.lg})`} />
			<source srcSet={promoBanner.mobile} />

			{/* Fallback <img> tag for maximum compatibility */}
			<img src={promoBanner.desktop} alt={promoBanner.alt} />
		</SC.Picture>
	)

	return (
		<>
			<SC.SummaryLayoutWrapper>
				<SC.ScrollableContent ref={scrollableContentRef}>
					<SC.SummaryWrapper>
						<SC.SummaryGrid>
							<SC.CloseButton type={'button'} onClick={() => onClose('button_x')} aria-label={messages['Close modal']}>
								<CloseIcon />
							</SC.CloseButton>
							{hasSummaryData && (
								<SC.PromoBanner ref={promoBannerRef}>
									<SC.PromoBannerContent>
										{promoBanner.link ? (
											<SC.PromoBannerLink href={promoBanner.link} {...getLinkAttributes(promoBanner.link, productionEnvironment, locale)}>
												{promoBannerPicture}
											</SC.PromoBannerLink>
										) : (
											promoBannerPicture
										)}
									</SC.PromoBannerContent>
								</SC.PromoBanner>
							)}
							<SC.ReservationContent>
								{(() => {
									if (!hasSummaryData) {
										return <ErrorScreen button={{ label: messages['Close booking modal'], onClick: () => onClose('button_close') }} />
									}

									const { salon } = reservations[0].calendarEvent
									const salonCoverImageUrl = salon.images?.find((image) => image.isCover)?.resizedImages.thumbnail

									return (
										<div>
											<SC.ReservationTitleWrapper>
												<SC.CheckIcon>
													<IconRegularCheckmark color={'icon.inverse'} width={'2.375rem'} height={'2.375rem'} />
												</SC.CheckIcon>
												<SC.ReservationTitle ref={titleRef} tabIndex={-1}>
													{messages['The reservation has been successfully created']}
												</SC.ReservationTitle>
											</SC.ReservationTitleWrapper>
											<SC.ReservationWrapper>
												{salon.logo && (
													<SC.SalonLogo>
														<img src={salon.logo.resizedImages.thumbnail} alt='salon logo' />
													</SC.SalonLogo>
												)}
												<SC.SalonInfo>
													{salonCoverImageUrl && (
														<SC.SalonCover>
															<img loading='lazy' src={salonCoverImageUrl} alt='salon cover' />
														</SC.SalonCover>
													)}
													<div>
														{salon.rating && (
															<SC.Rating>
																<VisuallyHidden>
																	{intl.formatMessage(
																		{
																			id: 'Average rating: {rating} out of 5',
																			defaultMessage: 'Average rating: {rating} out of 5'
																		},
																		{ rating: formatRating(intl, salon.rating) }
																	)}
																</VisuallyHidden>
																<span aria-hidden={'true'}>
																	<IconSolidStar
																		color={'icon.primary'}
																		width='12'
																		height='12'
																		style={{ marginBottom: '2px' }}
																	/>
																</span>
																<span aria-hidden={'true'}>{formatRating(intl, salon.rating)}</span>
															</SC.Rating>
														)}
														{salon.name && <SC.SalonTitle>{salon.name}</SC.SalonTitle>}
														<div>{salon.address && <SC.LineWithIcon>{salon.formattedAddress}</SC.LineWithIcon>}</div>
													</div>
												</SC.SalonInfo>
												<SC.Divider />
												<SC.ServicesList>
													{reservations.map((reservation) => {
														const {
															id: reservationID,
															state,
															price,
															calendarEvent: { service, start, end, employee }
														} = reservation
														const isSameReservationDay = start.date === end.date
														const employeeName = getEmployeeName(employee)
														const endTime = end.time
														const timeSlotLabel = endTime ? `${start.time} - ${endTime}` : start.time
														const showAddToCalendarButton = state === RESERVATION_STATE.APPROVED
														const reservationDisplayState = getReservationDisplayState(reservation)
														const statusTooltipContent = getReservationStatusTooltipContent(reservation, formatMessage, formatDate)

														return (
															<SC.ServiceListItem key={reservationID}>
																<SC.ServiceImageWrapper>
																	{service.previewImage?.resizedImages.thumbnail ? (
																		<SC.ServiceImage
																			src={service.previewImage.resizedImages.thumbnail}
																			alt={service.name || messages.Service}
																		/>
																	) : (
																		<SC.ServiceImageFallback />
																	)}
																</SC.ServiceImageWrapper>
																<SC.ServiceListItemBody>
																	<SC.ServiceListItemHeader>
																		<SC.ServiceListItemContent>
																			<SC.ServiceListItemTitleRow>
																				<SC.ServiceListItemTitle>
																					{service.name || messages.Service}
																				</SC.ServiceListItemTitle>
																				{reservationDisplayState && statusTooltipContent && (
																					<Tooltip
																						trigger={['hover']}
																						styles={{ root: { maxWidth: 280 } }}
																						content={
																							<SC.StatusTooltipContent>
																								{renderReservationStatusTooltipOverlay(statusTooltipContent)}
																							</SC.StatusTooltipContent>
																						}
																					>
																						<SC.ServiceStatusIconTrigger>
																							{reservationDisplayState === RESERVATION_STATE.APPROVED ? (
																								<CheckmarkIcon />
																							) : (
																								<SC.ClockIconWrapper />
																							)}
																						</SC.ServiceStatusIconTrigger>
																					</Tooltip>
																				)}
																			</SC.ServiceListItemTitleRow>
																			{isSameReservationDay ? (
																				<SC.ServiceMetaValue>{formatRelativeDate(start.date)}</SC.ServiceMetaValue>
																			) : (
																				<>
																					<SC.ServiceMetaValue>
																						{formatMessage(
																							{ id: 'From {date} {time}', defaultMessage: 'From {date} {time}' },
																							{ date: formatRelativeDate(start.date), time: start.time }
																						)}
																					</SC.ServiceMetaValue>
																					<SC.ServiceMetaValue>
																						{formatMessage(
																							{ id: 'To {date} {time}', defaultMessage: 'To {date} {time}' },
																							{ date: formatRelativeDate(end.date), time: end.time }
																						)}
																					</SC.ServiceMetaValue>
																				</>
																			)}
																			{isSameReservationDay && <SC.ServiceMetaValue>{timeSlotLabel}</SC.ServiceMetaValue>}
																			{employeeName && <SC.ServiceMetaValue>{employeeName}</SC.ServiceMetaValue>}
																			<SC.ServiceListItemPrice
																				from={price.priceFrom}
																				to={price.priceTo}
																				fromSecondary={price.priceFromSecondary}
																				toSecondary={price.priceToSecondary}
																			/>
																		</SC.ServiceListItemContent>
																		{showAddToCalendarButton && (
																			<SC.ServiceAddToCalendarIconButton
																				type={'button'}
																				aria-label={messages['Add to calendar']}
																				onClick={() => setCalendarReservation(reservation)}
																			>
																				<CalendarPlusIcon width={20} height={20} />
																			</SC.ServiceAddToCalendarIconButton>
																		)}
																	</SC.ServiceListItemHeader>
																</SC.ServiceListItemBody>
															</SC.ServiceListItem>
														)
													})}
												</SC.ServicesList>
												<SC.EmailNotificationDisclaimer>
													<InfoLine
														type={INFO_LINE_TYPE.INFO}
														message={formatMessage({
															id: 'We will send you a notification to your e-mail before the reserved dates.',
															defaultMessage: 'We will send you a notification to your e-mail before the reserved dates.'
														})}
													/>
												</SC.EmailNotificationDisclaimer>
												{customer && (
													<>
														<SC.DetailSection>
															<SC.DetailSectionTitle>{messages['Personal information']}</SC.DetailSectionTitle>
															<SC.DetailSectionLines>
																<SC.DetailSectionLine>
																	{customer.firstName} {customer.lastName}
																</SC.DetailSectionLine>
																<SC.DetailSectionLine $muted>{customer.email}</SC.DetailSectionLine>
																<SC.DetailSectionLine $muted>
																	{customer.phonePrefix} {customer.phone}
																</SC.DetailSectionLine>
															</SC.DetailSectionLines>
														</SC.DetailSection>
														{(hasVisitorSection || hasCustomerNoteSection) && <SC.Divider />}
													</>
												)}
												{hasVisitorSection && (
													<>
														<SC.DetailSection>
															<SC.DetailSectionTitle>{messages.Visitor}</SC.DetailSectionTitle>
															<SC.DetailSectionLines>
																<SC.DetailSectionLine>
																	{visitor.firstName} {visitor.lastName}
																</SC.DetailSectionLine>
															</SC.DetailSectionLines>
														</SC.DetailSection>
														{hasCustomerNoteSection && <SC.Divider />}
													</>
												)}
												{customerNote && (
													<SC.DetailSection>
														<SC.DetailSectionTitle>{messages.Note}</SC.DetailSectionTitle>
														<SC.DetailSectionText>{customerNote}</SC.DetailSectionText>
													</SC.DetailSection>
												)}
											</SC.ReservationWrapper>
										</div>
									)
								})()}
								{hasSummaryData && <SC.Footer>{footerButtons}</SC.Footer>}
							</SC.ReservationContent>
						</SC.SummaryGrid>
					</SC.SummaryWrapper>
				</SC.ScrollableContent>
				{hasSummaryData && <SC.MobileFooter>{footerButtons}</SC.MobileFooter>}
			</SC.SummaryLayoutWrapper>
			{calendarReservation && (
				<AddToCalendarModal
					show
					onClose={() => setCalendarReservation(null)}
					calendarFile={calendarReservation.calendarFile}
					reservationID={calendarReservation.id}
				/>
			)}
		</>
	)
}

export default Step4Summary
